<template>
  <div>
    <div class="flex flex-col sm:flex-row justify-between">
      <h1 class="font-bold text-2xl text-primary-500">Plantillas de Dietas</h1>
      <router-link
        :to="{ name: 'dietTemplatesForm' }"
        class="primary-button"
      >
        Crear Plantilla
      </router-link>
    </div>

    <div class="mt-4">
      <div class="flex flex-wrap gap-4">
        <select
          v-model="filters.template_type"
          class="input"
          @change="loadTemplates"
        >
          <option value="">Todos los tipos</option>
          <option value="transition">Transición</option>
          <option value="standard">Estándar</option>
          <option value="special">Especial</option>
        </select>

        <select
          v-model="filters.is_active"
          class="input"
          @change="loadTemplates"
        >
          <option value="">Todos los estados</option>
          <option value="1">Activas</option>
          <option value="0">Inactivas</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="mt-8 flex justify-center">
      <loader-icon class="w-8 h-8 animate-spin" />
    </div>

    <div v-else-if="templates.length === 0" class="mt-8 text-center text-gray-500">
      No se encontraron plantillas
    </div>

    <div v-else class="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="template in templates"
        :key="template.id"
        class="card"
      >
        <div>
          <h3 class="font-semibold text-lg">{{ template.name }}</h3>
          <div class="flex gap-2 mt-1">
            <span
              class="px-2 py-1 text-xs rounded"
              :class="{
                'bg-blue-100 text-blue-800': template.template_type === 'transition',
                'bg-green-100 text-green-800': template.template_type === 'standard',
                'bg-purple-100 text-purple-800': template.template_type === 'special'
              }"
            >
              {{ getTemplateTypeLabel(template.template_type) }}
            </span>
            <span
              v-if="!template.is_active"
              class="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600"
            >
              Inactiva
            </span>
          </div>
        </div>

        <p v-if="template.description" class="text-sm text-gray-600 mt-2">
          {{ template.description }}
        </p>

        <div v-if="template.duration_days" class="mt-3 text-sm">
          <p class="text-gray-600">
            Duración: {{ template.duration_days }} días
          </p>
        </div>

        <div class="mt-4 flex gap-2">
          <router-link
            :to="{ name: 'dietTemplatesDetail', params: { id: template.id } }"
            class="secondary-button text-sm"
          >
            Ver
          </router-link>
          <router-link
            :to="{ name: 'dietTemplatesEdit', params: { id: template.id } }"
            class="secondary-button text-sm"
          >
            Editar
          </router-link>
          <button
            class="delete-button text-sm"
            @click="confirmDelete(template)"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>

    <div v-if="pagination.last_page > 1" class="mt-8 flex justify-center gap-2">
      <button
        v-for="page in pagination.last_page"
        :key="page"
        class="px-3 py-1 rounded"
        :class="[
          page === pagination.current_page
            ? 'bg-primary-500 text-white'
            : 'bg-gray-200 hover:bg-gray-300'
        ]"
        @click="loadTemplates(page)"
      >
        {{ page }}
      </button>
    </div>

    <transition name="modal">
      <div v-if="templateToDelete" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-white rounded-lg p-6 max-w-md">
          <h3 class="text-lg font-semibold mb-4">Confirmar eliminación</h3>
          <p class="mb-4">
            ¿Estás seguro de que deseas eliminar la plantilla "{{ templateToDelete.name }}"?
          </p>
          <div class="flex justify-end gap-2">
            <button
              class="secondary-button"
              @click="templateToDelete = null"
            >
              Cancelar
            </button>
            <button
              class="delete-button"
              @click="deleteTemplate"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import LoaderIcon from "@/icons/LoaderIcon";
import { mapState } from "vuex";
import api from "@/api";

export default {
  name: "DietTemplatesIndex",

  components: {
    LoaderIcon
  },

  data() {
    return {
      templates: [],
      loading: false,
      filters: {
        template_type: "",
        is_active: ""
      },
      pagination: {
        current_page: 1,
        last_page: 1
      },
      templateToDelete: null
    };
  },

  computed: {
    ...mapState({
      user: "user"
    })
  },

  mounted() {
    this.$store.commit("setSelectedTab", "dietTemplates");
    this.loadTemplates();
  },

  methods: {
    hasPermission(permission) {
      return this.user?.permissions?.includes(permission) || false;
    },

    getTemplateTypeLabel(type) {
      const labels = {
        transition: "Transición",
        standard: "Estándar",
        special: "Especial"
      };
      return labels[type] || type;
    },

    async loadTemplates(page = 1) {
      this.loading = true;
      try {
        const params = [
          { name: 'page', value: page },
          { name: 'template_type', value: this.filters.template_type },
          { name: 'is_active', value: this.filters.is_active }
        ];
        const response = await api.dietTemplates.getAllWithQueryParams(params);
        this.templates = response.data;
        this.pagination = {
          current_page: response.current_page,
          last_page: response.last_page
        };
      } catch (error) {
        console.error("Error loading templates:", error);
        this.$store.commit("setSnack", {
          message: "Error al cargar las plantillas",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(template) {
      this.templateToDelete = template;
    },

    async deleteTemplate() {
      if (!this.templateToDelete) return;

      try {
        await api.dietTemplates.delete(this.templateToDelete.id);
        this.$store.commit("setSnack", {
          message: "Plantilla eliminada exitosamente",
          color: "success"
        });
        this.templateToDelete = null;
        this.loadTemplates();
      } catch (error) {
        this.$store.commit("setSnack", {
          message: "Error al eliminar la plantilla",
          color: "error"
        });
      }
    }
  }
};
</script>